import { useMemo, useState } from 'react';
import type { BusinessAiEscalationListItem, PublicAiEscalationStatus } from '../../shared/types';
import { Badge, Card, EmptyState, HeroSelectField, LoadingScreen, PageHeader } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessAiEscalations, useBusinessBranches } from '../hooks/businessData';

const statusLabels: Record<PublicAiEscalationStatus, string> = {
  open: 'Abierta',
  resolved: 'Resuelta',
};

const statusTone: Record<PublicAiEscalationStatus, 'amber' | 'green'> = {
  open: 'amber',
  resolved: 'green',
};

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('es', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function EscalationCard({ escalation }: { escalation: BusinessAiEscalationListItem }) {
  return (
    <Card className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted">{formatDate(escalation.createdAt)}</p>
          <h3 className="mt-1 text-lg font-black text-foreground">{escalation.customerName}</h3>
          <p className="text-sm font-semibold text-muted">{escalation.branchName}</p>
        </div>
        <Badge tone={statusTone[escalation.status]}>{statusLabels[escalation.status]}</Badge>
      </div>
      <div className="grid gap-3 sm:grid-cols-[220px_minmax(0,1fr)]">
        <div className="rounded-2xl bg-surface-secondary p-3">
          <p className="text-xs font-bold uppercase tracking-wide text-muted">Teléfono</p>
          <a className="mt-1 block font-black text-foreground" href={`tel:${escalation.customerPhone}`}>
            {escalation.customerPhone}
          </a>
        </div>
        <div className="rounded-2xl border border-border bg-surface-secondary p-3">
          <p className="text-xs font-bold uppercase tracking-wide text-muted">Queja</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-foreground">{escalation.complaint}</p>
        </div>
      </div>
    </Card>
  );
}

export default function AiEscalationsPage() {
  const { user } = useAuth();
  const canChooseBranch = user?.role === 'owner' || user?.role === 'manager';
  const [branchId, setBranchId] = useState(canChooseBranch ? '' : (user?.branchId ?? ''));
  const [status, setStatus] = useState<PublicAiEscalationStatus | ''>('open');
  const { data: allBranches = [] } = useBusinessBranches(canChooseBranch);
  const { data: escalations = [], isLoading, isFetching } = useBusinessAiEscalations(branchId, status);

  const branchOptions = useMemo(() => [
    { id: '', label: 'Todas las sucursales' },
    ...allBranches.filter((branch) => branch.active).map((branch) => ({ id: branch.id, label: branch.name })),
  ], [allBranches]);

  if (isLoading) return <LoadingScreen label="Cargando alertas..." />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alertas del asistente IA"
        description="Solicitudes de clientes que pidieron ayuda humana desde el chat público."
      />

      <Card className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
        <HeroSelectField
          isDisabled={!canChooseBranch}
          label="Sucursal"
          options={branchOptions}
          value={branchId || null}
          onChange={(value) => setBranchId(String(value ?? ''))}
        />
        <HeroSelectField
          label="Estado"
          options={[{ id: '', label: 'Todos los estados' }, { id: 'open', label: 'Abiertas' }, { id: 'resolved', label: 'Resueltas' }]}
          value={status || null}
          onChange={(value) => setStatus(String(value ?? '') as PublicAiEscalationStatus | '')}
        />
      </Card>

      {isFetching ? <p className="text-sm font-semibold text-muted">Actualizando alertas...</p> : null}

      {escalations.length ? (
        <div className="grid gap-4">
          {escalations.map((escalation) => <EscalationCard key={escalation.id} escalation={escalation} />)}
        </div>
      ) : (
        <EmptyState title="No hay alertas" description="Cuando un cliente pida ayuda desde el asistente, aparecerá aquí." />
      )}
    </div>
  );
}
